import { Injectable } from '@angular/core';
import BigNumber from 'bignumber.js';
import { BehaviorSubject, combineLatest } from 'rxjs';

import config from '../../data/config.json';
import nftStakingAbi from '../../data/nft-staking-abi.json';
import { BlocknativeService } from '../blocknative.service';
import { ButtonActiveService } from '../button-active.service';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class NftStakingService {
  private contract: any;

  private _stakingData = new BehaviorSubject<Array<NftStakeDataItem>>([]);
  stakingData$ = this._stakingData.asObservable();

  constructor(
    private bncService: BlocknativeService,
    private buttonActiveSvc: ButtonActiveService,
    private tokenService: TokenService
  ) {
    this._setContract();
  }

  async stakeToken(value: BigNumber, planId: number) {
    this.buttonActiveSvc.staking.next(true);
    const allowance = await this.tokenService.getAllowance(
      this.contract._address
    );

    if (new BigNumber(allowance).isLessThan(value)) {
      await this.tokenService.approve(this.contract._address, value.toFixed());
    }

    await this.contract.methods
      .stake(value.toFixed(), planId)
      .send({ from: this.bncService.address, gasPrice: 5_000_000_000 });

    let x = false;
    while (!x) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      x = await this.getStakingData()
        .then(() => true)
        .catch(() => false);
    }
    this.tokenService.refresh();
    this.buttonActiveSvc.staking.next(false);
  }

  async unstakeToken(tokenId: string) {
    this.buttonActiveSvc.unstaking.next(true);

    await this.contract.methods
      .unstake([tokenId])
      .send({ from: this.bncService.address, gasPrice: 5_000_000_000 });

    this.getStakingData();
    this.tokenService.refresh();
    this.buttonActiveSvc.unstaking.next(false);
  }

  private async getStakingData() {
    const stakes = await Promise.all(
      (
        await this.contract.methods
          .getStakesForUser(this.bncService.address)
          .call()
      ).map(async (stake: any, i: number) => {
        const tokenId = await this.contract.methods
          .tokenOfOwnerByIndex(this.bncService.address, i)
          .call();

        const metadataUri = await this.contract.methods
          .tokenURI(tokenId)
          .call();

        const plan = await this.contract.methods
          .getPlanById(stake.planId)
          .call();

        const lockedUntil = +stake.stakedAt + +plan.lockDuration;

        const reward = new BigNumber(stake.amount)
          .multipliedBy(plan.apy)
          .multipliedBy(
            Math.floor((Math.floor(Date.now() / 1000) - stake.stakedAt) / 86400)
          )
          .div(36500);

        console.log(
          new BigNumber(plan.apy)
            .multipliedBy(stake.amount)
            .dividedBy(100)
            .toString()
        );

        return {
          ...stake,
          tokenId,
          reward,
          lockedUntil: new Date(lockedUntil * 1000),
          image: await fetch(metadataUri)
            .then((data) => data.json())
            .then((data) => data.image),
        };
      })
    );

    this._stakingData.next(stakes as NftStakeDataItem[]);
  }

  private _setContract() {
    combineLatest([
      this.bncService.address$,
      this.bncService.networkId$,
    ]).subscribe(([address, networkId]) => {
      if (!networkId || !address) {
        return;
      }

      const network = config.networks.find((n) => n.networkId === networkId);

      if (!network) {
        return;
      }

      this.contract = new this.bncService.web3.eth.Contract(
        nftStakingAbi,
        network.nftStakingContractAddress
      );

      if (!this.contract._address) {
        return;
      }

      this.getStakingData();
    });
  }
}

export interface NftStakeDataItem {
  amount: string;
  claimed: string;
  image: string;
  planId: string;
  stakedAt: string;
  tokenId: string;
  unstakedAt: string;
  lockedUntil: Date;
  usdcReceived: string;
  reward: BigNumber;
}
